import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Trophy, Users, Zap, TrendingUp, Medal, Shield, Star, ChevronRight, Play } from 'lucide-react';

const LEADERBOARD = [
  { rank: 1, name: 'Alex Rodriguez', sport: '🏀', pts: 2840, trend: '+12', medal: '🥇' },
  { rank: 2, name: 'Sarah Chen', sport: '🎾', pts: 2710, trend: '+8', medal: '🥈' },
  { rank: 3, name: 'Marcus Johnson', sport: '⚽', pts: 2650, trend: '+5', medal: '🥉' },
  { rank: 4, name: 'Priya Patel', sport: '🏐', pts: 2480, trend: '+3', medal: null },
  { rank: 5, name: 'Jordan Smith', sport: '🏸', pts: 2310, trend: '+7', medal: null },
];

const LIVE_MATCHES = [
  { sport: '🏀', t1: 'Midnight Ballers', t2: 'Court Kings', s1: 87, s2: 82, period: 'Q4 · 2:30', hot: true },
  { sport: '⚽', t1: 'FC United', t2: 'City FC', s1: 2, s2: 1, period: 'HT', hot: false },
  { sport: '🎾', t1: 'A. Smith', t2: 'J. Lee', s1: 6, s2: 4, period: 'Set 2', hot: true },
];

const ACHIEVEMENTS = [
  { icon: '🏆', title: 'Tournament Champion', desc: 'Win your first tournament' },
  { icon: '🔥', title: 'Hot Streak', desc: '5 wins in a row' },
  { icon: '⚡', title: 'Speed Scorer', desc: 'Score 20+ points in one match' },
  { icon: '🦁', title: 'Squad Leader', desc: 'Lead a squad of 10+ members' },
  { icon: '🎯', title: 'Sharpshooter', desc: '90%+ accuracy over a season' },
  { icon: '👑', title: 'League King', desc: 'Top rank for an entire season' },
];

export default function LandingV3Page() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [activeLive, setActiveLive] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setActiveLive(a => (a + 1) % LIVE_MATCHES.length), 3000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const handleMouse = (e) => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      setMousePos({
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
      });
    };
    window.addEventListener('mousemove', handleMouse);
    return () => window.removeEventListener('mousemove', handleMouse);
  }, []);

  return (
    <div style={{ backgroundColor: '#030712', color: '#fff', minHeight: '100vh', fontFamily: 'Inter, system-ui, sans-serif', overflowX: 'hidden' }}>
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(40px)} to{opacity:1;transform:translateY(0)} }
        @keyframes glow { 0%,100%{opacity:0.6} 50%{opacity:1} }
        @keyframes scan { 0%{transform:translateY(-100%)} 100%{transform:translateY(800%)} }
        @keyframes countUp { from{opacity:0;transform:scale(0.8)} to{opacity:1;transform:scale(1)} }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.2} }
        @keyframes slideLeft { from{transform:translateX(30px);opacity:0} to{transform:translateX(0);opacity:1} }
        @keyframes marquee { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
        .champions-text {
          background: linear-gradient(180deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .blue-glow-text {
          background: linear-gradient(135deg, #93c5fd, #60a5fa, #3b82f6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .glass { background: rgba(255,255,255,0.04); backdrop-filter: blur(12px); border: 1px solid rgba(255,255,255,0.08); }
        .gold-border { border: 1px solid rgba(251,191,36,0.3); }
        .card-lift { transition: transform 0.3s ease, box-shadow 0.3s ease; }
        .card-lift:hover { transform: translateY(-4px); box-shadow: 0 20px 60px rgba(0,0,0,0.5); }
        .live-dot { animation: blink 1s infinite; }
        .scan-line { position:absolute; width:100%; height:2px; background:linear-gradient(90deg,transparent,rgba(59,130,246,0.3),transparent); animation:scan 4s linear infinite; }
        .marquee-track { display:flex; animation:marquee 20s linear infinite; width:max-content; }
        .achievement-card { transition: all 0.3s ease; cursor:default; }
        .achievement-card:hover { transform:scale(1.05); border-color:rgba(251,191,36,0.4) !important; background:rgba(251,191,36,0.06) !important; }
      `}</style>

      {/* === TOP STATUS BAR === */}
      <div className="flex items-center justify-between px-6 py-2 text-xs font-bold" style={{ background: 'rgba(59,130,246,0.08)', borderBottom: '1px solid rgba(59,130,246,0.15)' }}>
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1.5" style={{ color: '#4ade80' }}>
            <span className="live-dot w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
            SYSTEM ONLINE
          </span>
          <span style={{ color: '#6b7280' }}>|</span>
          <span style={{ color: '#60a5fa' }}>3 LIVE MATCHES</span>
          <span style={{ color: '#6b7280' }}>|</span>
          <span style={{ color: '#a78bfa' }}>127 PLAYERS ONLINE</span>
        </div>
        <button onClick={() => navigate('/signin')} style={{ color: '#60a5fa', textDecoration: 'underline' }}>
          SIGN IN →
        </button>
      </div>

      {/* === HERO === */}
      <section ref={heroRef} className="relative overflow-hidden flex items-center"
        style={{ minHeight: '100vh', background: 'radial-gradient(ellipse 100% 80% at 50% -10%, rgba(59,130,246,0.15) 0%, transparent 60%)' }}>
        {/* Animated grid background */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'linear-gradient(rgba(59,130,246,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.05) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
          maskImage: 'radial-gradient(ellipse 80% 80% at 50% 0%, black 0%, transparent 100%)',
        }} />
        {/* Mouse parallax glow */}
        <div style={{
          position: 'absolute', width: '600px', height: '600px', borderRadius: '50%', pointerEvents: 'none',
          background: 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)',
          transform: `translate(${mousePos.x * 60 - 30}px, ${mousePos.y * 60 - 30}px)`,
          left: '50%', top: '30%', marginLeft: '-300px', marginTop: '-300px',
          transition: 'transform 0.3s ease',
        }} />

        <div className="max-w-7xl mx-auto px-6 py-24 w-full grid lg:grid-cols-5 gap-12 items-center">
          {/* Left col (3/5) */}
          <div className="lg:col-span-3" style={{ animation: 'fadeUp 0.8s ease forwards' }}>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 glass gold-border text-sm font-bold"
              style={{ color: '#fbbf24' }}>
              <Trophy size={14} />
              The #1 Sports Management Platform
            </div>

            <h1 className="font-black leading-none mb-6" style={{ fontSize: 'clamp(3.5rem,8vw,6.5rem)', letterSpacing: '-0.03em' }}>
              <span style={{ color: '#fff' }}>COMPETE.</span>
              <br />
              <span className="champions-text">DOMINATE.</span>
              <br />
              <span className="blue-glow-text">CHAMPION.</span>
            </h1>

            <p className="text-lg leading-relaxed mb-10" style={{ color: '#9ca3af', maxWidth: '520px' }}>
              Kynaix Sports is where serious athletes run tournaments, build elite squads,
              track live scores, and claim their place on the leaderboard.
              <strong style={{ color: '#fff' }}> No limits. No fees. Just wins.</strong>
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <button
                onClick={() => navigate(isAuthenticated ? '/tournaments' : '/signup')}
                className="flex items-center justify-center gap-3 px-8 py-4 rounded-2xl font-black text-lg transition-all"
                style={{
                  background: 'linear-gradient(135deg, #1d4ed8, #2563eb, #3b82f6)',
                  color: '#fff',
                  boxShadow: '0 8px 40px rgba(37,99,235,0.5)',
                  border: '1px solid rgba(96,165,250,0.3)',
                }}>
                <Trophy size={20} />
                Enter the Arena
              </button>
              <button
                onClick={() => navigate('/signup')}
                className="flex items-center justify-center gap-2 px-8 py-4 rounded-2xl font-bold text-lg glass"
                style={{ color: '#fff' }}>
                <Play size={16} fill="#fff" /> Watch Demo
              </button>
            </div>

            {/* Stats row */}
            <div className="flex flex-wrap gap-8">
              {[
                { n: '10K+', l: 'Athletes', c: '#60a5fa' },
                { n: '500+', l: 'Tournaments', c: '#fbbf24' },
                { n: '50+', l: 'Cities', c: '#4ade80' },
              ].map(s => (
                <div key={s.l} className="text-center">
                  <div className="text-3xl font-black" style={{ color: s.c }}>{s.n}</div>
                  <div className="text-xs font-bold uppercase tracking-widest" style={{ color: '#6b7280' }}>{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right col (2/5): Live panel */}
          <div className="lg:col-span-2 space-y-4" style={{ animation: 'slideLeft 0.8s ease forwards 0.3s', opacity: 0 }}>
            {/* Live Matches */}
            <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(59,130,246,0.25)', background: 'rgba(15,23,42,0.8)', backdropFilter: 'blur(12px)' }}>
              <div className="flex items-center gap-2 px-4 py-3" style={{ background: 'rgba(59,130,246,0.1)', borderBottom: '1px solid rgba(59,130,246,0.15)' }}>
                <span className="live-dot w-2 h-2 rounded-full bg-red-500 inline-block" />
                <span className="text-xs font-black text-red-400 uppercase tracking-widest">Live Matches</span>
              </div>
              <div className="relative overflow-hidden" style={{ minHeight: '160px' }}>
                <div className="scan-line" />
                {LIVE_MATCHES.map((m, i) => (
                  <div key={i}
                    className="absolute inset-0 p-5 flex flex-col justify-between transition-all duration-500"
                    style={{ opacity: i === activeLive ? 1 : 0, transform: `translateY(${i === activeLive ? 0 : 20}px)` }}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-2xl">{m.sport}</span>
                      <div className="flex items-center gap-2">
                        {m.hot && <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: 'rgba(239,68,68,0.15)', color: '#f87171' }}>🔥 HOT</span>}
                        <span className="text-xs" style={{ color: '#6b7280' }}>{m.period}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-bold">{m.t1}</span>
                        <span className="font-black text-2xl" style={{ color: '#60a5fa' }}>{m.s1}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-bold" style={{ color: '#9ca3af' }}>{m.t2}</span>
                        <span className="font-black text-2xl" style={{ color: '#9ca3af' }}>{m.s2}</span>
                      </div>
                    </div>
                    <div className="flex gap-1 mt-3">
                      {LIVE_MATCHES.map((_, j) => (
                        <div key={j} className="h-1 rounded-full flex-1 transition-all"
                          style={{ background: j === activeLive ? '#3b82f6' : 'rgba(255,255,255,0.1)' }} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Leaderboard */}
            <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(251,191,36,0.2)', background: 'rgba(15,23,42,0.8)', backdropFilter: 'blur(12px)' }}>
              <div className="flex items-center gap-2 px-4 py-3" style={{ background: 'rgba(251,191,36,0.06)', borderBottom: '1px solid rgba(251,191,36,0.15)' }}>
                <Trophy size={14} style={{ color: '#fbbf24' }} />
                <span className="text-xs font-black uppercase tracking-widest" style={{ color: '#fbbf24' }}>Top Players</span>
              </div>
              <div className="divide-y" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
                {LEADERBOARD.map((p) => (
                  <div key={p.rank} className="flex items-center gap-3 px-4 py-2.5">
                    <div className="text-lg w-6 text-center flex-shrink-0">
                      {p.medal || <span className="text-sm font-bold" style={{ color: '#6b7280' }}>#{p.rank}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{p.name}</div>
                    </div>
                    <span className="text-lg">{p.sport}</span>
                    <div className="text-right">
                      <div className="font-black text-sm" style={{ color: '#60a5fa' }}>{p.pts.toLocaleString()}</div>
                      <div className="text-xs font-bold" style={{ color: '#4ade80' }}>{p.trend}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-4 py-3" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <button onClick={() => navigate(isAuthenticated ? '/' : '/signup')} className="text-xs font-bold w-full text-center" style={{ color: '#60a5fa' }}>
                  View Full Leaderboard →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === SPORTS MARQUEE === */}
      <div style={{ overflow: 'hidden', padding: '16px 0', borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.02)' }}>
        <div className="marquee-track">
          {['🏀 Basketball', '⚽ Soccer', '🎾 Tennis', '🏐 Volleyball', '🏸 Pickleball', '🏏 Cricket', '🏓 Ping Pong', '🏒 Hockey', '⛳ Golf', '🎰 Poker', '⚾ Baseball', '🏊 Swimming',
            '🏀 Basketball', '⚽ Soccer', '🎾 Tennis', '🏐 Volleyball', '🏸 Pickleball', '🏏 Cricket', '🏓 Ping Pong', '🏒 Hockey', '⛳ Golf', '🎰 Poker', '⚾ Baseball', '🏊 Swimming'].map((s, i) => (
            <span key={i} className="font-black text-sm uppercase tracking-widest px-8" style={{ color: 'rgba(255,255,255,0.2)', whiteSpace: 'nowrap' }}>
              {s}
            </span>
          ))}
        </div>
      </div>

      {/* === FEATURE SHOWCASE === */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-black uppercase tracking-widest mb-3" style={{ color: '#60a5fa' }}>Built for Champions</p>
            <h2 className="font-black mb-4" style={{ fontSize: 'clamp(2rem,5vw,3.5rem)' }}>
              Every Tool You Need to <span className="champions-text">Win</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Trophy, title: 'Tournaments', desc: 'Bracket, round-robin, leagues. Full automation from registration to champions.', color: '#fbbf24', cols: 1 },
              { icon: Users, title: 'Squads', desc: 'Build your crew, chat real-time, track expenses, coordinate games.', color: '#60a5fa', cols: 1 },
              { icon: Zap, title: 'Live Scoring', desc: 'Real-time scores for 10+ sports. Every point. Every set. Live.', color: '#4ade80', cols: 1 },
              { icon: TrendingUp, title: 'Analytics & Stats', desc: 'Win rates, point averages, ranking progression, personal records, and more.', color: '#f472b6', cols: 1 },
              { icon: Medal, title: 'Achievements', desc: 'Earn badges, climb rankings, unlock trophies as you compete and grow.', color: '#fb923c', cols: 1 },
              { icon: Shield, title: 'Marketplace', desc: 'Buy and sell sports gear. Find deals on equipment from your local community.', color: '#a78bfa', cols: 1 },
            ].map((f) => (
              <div key={f.title} className="card-lift rounded-2xl p-6 glass">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: f.color + '15', border: `1px solid ${f.color}30` }}>
                  <f.icon size={20} style={{ color: f.color }} />
                </div>
                <h3 className="font-bold text-lg mb-2">{f.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#9ca3af' }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === ACHIEVEMENTS === */}
      <section className="py-24 px-6" style={{ background: 'rgba(251,191,36,0.03)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm font-black uppercase tracking-widest mb-3" style={{ color: '#fbbf24' }}>Gamification</p>
            <h2 className="font-black text-4xl mb-4">Earn Your <span className="champions-text">Glory</span></h2>
            <p style={{ color: '#6b7280' }}>Every win, every streak, every milestone — rewarded.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {ACHIEVEMENTS.map((a) => (
              <div key={a.title} className="achievement-card text-center p-4 rounded-2xl glass gold-border">
                <div className="text-4xl mb-2">{a.icon}</div>
                <div className="font-bold text-sm mb-1">{a.title}</div>
                <div className="text-xs" style={{ color: '#6b7280' }}>{a.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === FINAL CTA === */}
      <section className="py-32 px-6 text-center relative overflow-hidden">
        {/* Championship ring graphic */}
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          width: '600px', height: '600px', borderRadius: '50%', pointerEvents: 'none',
          border: '1px solid rgba(251,191,36,0.08)',
          boxShadow: '0 0 0 30px rgba(251,191,36,0.03), 0 0 0 60px rgba(251,191,36,0.02), 0 0 0 100px rgba(251,191,36,0.01)',
        }} />
        <div className="relative max-w-3xl mx-auto">
          <div className="text-7xl mb-6">👑</div>
          <h2 className="font-black mb-6" style={{ fontSize: 'clamp(2.5rem,6vw,4.5rem)', letterSpacing: '-0.02em' }}>
            The Champions <br />
            <span className="champions-text">Start Here.</span>
          </h2>
          <p className="text-xl mb-10" style={{ color: '#9ca3af', maxWidth: '500px', margin: '0 auto 2.5rem' }}>
            Join 10,000+ athletes competing on Kynaix Sports. Your first tournament is free. Your name on the leaderboard is priceless.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/signup')}
              className="px-12 py-5 rounded-2xl font-black text-xl transition-all"
              style={{
                background: 'linear-gradient(135deg, #1d4ed8, #2563eb)',
                color: '#fff',
                boxShadow: '0 0 80px rgba(37,99,235,0.4), inset 0 1px 0 rgba(255,255,255,0.15)',
                border: '1px solid rgba(96,165,250,0.3)',
              }}>
              Enter the Arena — Free
            </button>
            <button
              onClick={() => navigate('/tournaments')}
              className="px-12 py-5 rounded-2xl font-bold text-xl glass"
              style={{ color: '#fff' }}>
              Browse Tournaments
            </button>
          </div>
          <p className="mt-6 text-sm" style={{ color: '#374151' }}>Free forever · No credit card · 2 minute setup</p>
        </div>
      </section>

      <footer className="py-10 px-6 text-center" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="font-black text-2xl mb-2 champions-text">Kynaix Sports</div>
        <p style={{ color: '#374151', fontSize: '0.875rem' }}>Compete. Dominate. Champion.</p>
      </footer>
    </div>
  );
}
